import React, { useEffect, useState } from "react";
import { supabase } from "./supabase";

export const DraftBoard = ({ sessionId }: { sessionId?: string }) => {
  const [picks, setPicks] = useState<any[]>([]);
  const [activeRound, setActiveRound] = useState(1);
  const totalRounds = 22;

  useEffect(() => {
    if (!sessionId) return;

    fetchPicks();

    const channel = supabase
      .channel(`board-sync-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "picks",
          filter: `session_id=eq.${sessionId}`,
        },
        () => fetchPicks()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  const fetchPicks = async () => {
    if (!sessionId) return;

    const { data } = await supabase
      .from("picks")
      .select(
        `
        id, pick_number, round_number,
        player:players(name, position, hometown),
        team:teams(team_name, primary_color)
      `
      )
      .eq("session_id", sessionId)
      .order("pick_number", { ascending: true });

    if (data) {
      setPicks(data);
      // Auto-set the tab to the latest round being drafted
      if (data.length > 0) {
        const latestRound = Math.ceil((data.length + 1) / 4);
        setActiveRound(latestRound <= 22 ? latestRound : latestRound);
      }
    }
  };

  // Helper to get picks for a specific round
  const getPicksForRound = (roundNum: number) => {
    return picks.filter((p) => p.round_number === roundNum);
  };

  return (
    <div
      className="draft-board-container"
      style={{
        marginBottom: "40px",
        background: "#000",
        borderRadius: "12px",
        border: "2px solid #22565a",
        overflow: "hidden",
        fontFamily: "'Oswald', sans-serif",
      }}
    >
      {/* ROUND TABS (Scrollable) */}
      <div
        className="round-tabs"
        style={{
          display: "flex",
          overflowX: "auto",
          background: "#151513",
          borderBottom: "1px solid #22565a",
          padding: "10px",
        }}
      >
        {Array.from({ length: totalRounds }, (_, i) => i + 1).map((round) => (
          <button
            key={round}
            onClick={() => setActiveRound(round)}
            style={{
              padding: "8px 15px",
              marginRight: "5px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontWeight: "bold",
              fontSize: "0.8rem",
              background: activeRound === round ? "#22565a" : "transparent",
              color: activeRound === round ? "#f9eeda" : "#666",
              transition: "0.3s",
              fontFamily: "'Oswald', sans-serif",
              textTransform: "uppercase",
            }}
          >
            ROUND {round}
          </button>
        ))}
      </div>

      {/* ROUND HEADER */}
      <div
        className="round-header"
        style={{
          padding: "20px",
          background: "#0a0a0a",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2
          style={{
            margin: 0,
            color: "#f9eeda",
            fontSize: "1.5rem",
            textTransform: "uppercase",
          }}
        >
          ROUND {activeRound} RESULTS
        </h2>
        <span
          className="picks-counter"
          style={{ color: "#22565a", fontWeight: "bold", letterSpacing: "1px" }}
        >
          {getPicksForRound(activeRound).length} / 4 PICKS MADE
        </span>
      </div>

      {/* PICKS LIST */}
      <div
        className="picks-list-wrapper"
        style={{ padding: "0 20px 20px 20px" }}
      >
        <div
          className="picks-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "15px",
          }}
        >
          {Array.from({ length: 4 }, (_, i) => {
            const expectedPickNum = (activeRound - 1) * 4 + (i + 1);
            const pick = getPicksForRound(activeRound).find(
              (p) => p.pick_number === expectedPickNum
            );

            return (
              <div
                key={expectedPickNum}
                style={{
                  background: "#1a1a1a",
                  padding: "15px",
                  borderRadius: "6px",
                  border: pick
                    ? `1px solid ${pick.team.primary_color}`
                    : "1px dashed #333",
                  opacity: pick ? 1 : 0.5,
                  transition: "all 0.3s ease",
                }}
              >
                <div
                  style={{
                    fontSize: "0.7rem",
                    color: "#22565a",
                    fontWeight: "bold",
                    marginBottom: "5px",
                  }}
                >
                  PICK #{expectedPickNum}
                </div>
                {pick ? (
                  <>
                    <div
                      style={{
                        color: "#f9eeda",
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      {pick.player.name}
                    </div>
                    <div
                      style={{
                        color: pick.team.primary_color,
                        fontSize: "0.8rem",
                        textTransform: "uppercase",
                        marginTop: "3px",
                        fontWeight: 600,
                      }}
                    >
                      {pick.team.team_name}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "8px",
                        fontSize: "0.75rem",
                        color: "#888",
                        textTransform: "uppercase",
                        borderTop: "1px solid #333",
                        paddingTop: "8px",
                      }}
                    >
                      <span style={{ color: "#22565a", fontWeight: "bold" }}>
                        {pick.player.position}
                      </span>
                      <span>|</span>
                      <span>{pick.player.hometown}</span>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      color: "#444",
                      fontStyle: "italic",
                      fontSize: "0.9rem",
                    }}
                  >
                    Waiting for Pick...
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .round-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 5px;
            padding: 15px !important;
          }
          .round-header h2 {
            font-size: 1.2rem !important;
          }
          .picks-counter {
            font-size: 0.8rem;
          }
          .picks-grid {
            grid-template-columns: 1fr !important;
          }
          .picks-list-wrapper {
            padding: 0 15px 15px 15px !important;
          }
          .round-tabs {
            padding: 8px !important;
          }
          .round-tabs button {
            padding: 6px 12px !important;
            font-size: 0.7rem !important;
          }
        }
      `}</style>
    </div>
  );
};
